import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { User, Ticket as TicketIcon, Plus } from 'lucide-react';
import DashboardLayout from '../components/layout/DashboardLayout';
import Container from '../components/layout/Container';
import TicketList from '../components/tickets/TicketList';
import TicketTable from '../components/tickets/TicketTable';
import TicketKanbanBoard from '../components/tickets/TicketKanbanBoard';
import DateRangePicker from '../components/layout/DateRangePicker';
import Pagination from '../components/common/Pagination';

import { useTickets } from '../hooks/useTickets';
import { useAuthContext } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useTicketRefresh } from '../contexts/TicketRefreshContext';

import { loadDateRange, saveDateRange } from '../utils/dateRangePersistence';
import { Ticket } from '../types/ticket';
import { TICKET_STATUSES } from '../constants/ticketConstants';
import { Status } from '../types';
import { ticketService } from '../services/ticketService';

const ITEMS_PER_PAGE = 12;

export default function MyWorkspacePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const { refreshTrigger } = useTicketRefresh();

  const { t } = useTranslation();
  const { user } = useAuthContext();

  const [dateRange, setDateRange] = useState(loadDateRange('my-workspace-date-range'));
  const [activeTab, setActiveTab] = useState<'open' | 'assigned'>('open');
  const [viewMode, setViewMode] = useState<'list' | 'kanban' | 'table'>(() => {
    const savedMode = localStorage.getItem('myWorkspaceViewMode');
    return (savedMode === 'list' || savedMode === 'kanban' || savedMode === 'table') ? savedMode : 'kanban';
  });

  // Sync view mode with localStorage on toggle
  const toggleViewMode = (mode: 'list' | 'kanban' | 'table') => {
    setViewMode(mode);
    localStorage.setItem('myWorkspaceViewMode', mode);
  };

  // Get filter states from location state or localStorage
  const filterState = location.state?.filterState || {
    selectedStatus: localStorage.getItem('myWorkspaceSelectedStatus') || '',
    searchQuery: localStorage.getItem('myWorkspaceSearchQuery') || '',
    sortBy: localStorage.getItem('myWorkspaceSortBy') || 'createdAt',
    sortOrder: localStorage.getItem('myWorkspaceSortOrder') || 'desc',
    currentPage: parseInt(localStorage.getItem('myWorkspaceCurrentPage') || '1')
  };

  // Filter states for open tickets (as requester)
  const [openCurrentPage, setOpenCurrentPage] = useState(1);
  const [openSelectedStatus, setOpenSelectedStatus] = useState(filterState.selectedStatus);
  const [openSortBy, setOpenSortBy] = useState('createdAt');
  const [openSortOrder, setOpenSortOrder] = useState<'asc' | 'desc'>('desc');

  // Filter states for assigned tickets (as agent)
  const [assignedCurrentPage, setAssignedCurrentPage] = useState(1);
  const [assignedSelectedStatus, setAssignedSelectedStatus] = useState<string>(filterState.selectedStatus);
  const [assignedSortBy, setAssignedSortBy] = useState('createdAt');
  const [assignedSortOrder, setAssignedSortOrder] = useState<'asc' | 'desc'>('desc');

  const handleStatusChange = (status: string, tab: 'open' | 'assigned') => {
    if (tab === 'open') {
      setOpenSelectedStatus(status);
    } else {
      setAssignedSelectedStatus(status);
    }
    localStorage.setItem('myWorkspaceSelectedStatus', status);
  };

  const handleSortChange = (sortBy: string, tab: 'open' | 'assigned') => {
    if (tab === 'open') {
      setOpenSortBy(sortBy);
    } else {
      setAssignedSortBy(sortBy);
    }
    localStorage.setItem('myWorkspaceSortBy', sortBy);
  };

  const handleSortOrderChange = (sortOrder: 'asc' | 'desc', tab: 'open' | 'assigned') => {
    if (tab === 'open') {
      setOpenSortOrder(sortOrder);
    } else {
      setAssignedSortOrder(sortOrder);
    }
    localStorage.setItem('myWorkspaceSortOrder', sortOrder);
  };

  // Save date range whenever it changes
  useEffect(() => {
    saveDateRange('my-workspace-date-range', dateRange);
  }, [dateRange]);

  // Save view mode when it changes
  useEffect(() => {
    localStorage.setItem('myWorkspaceViewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    if (refreshTrigger > 0) {
      fetchOpenTickets();
      fetchAssignedTickets();
    }
  }, [refreshTrigger]);


  // Get tickets where current user is the requester (created by user)
  const {
    tickets: openTickets,
    totalCount: openTotalCount,
    isLoading: openLoading,
    stats: openStats,
    fetchTickets: fetchOpenTickets
  } = useTickets({
    dateRange: dateRange,
    page: openCurrentPage,
    limit: ITEMS_PER_PAGE,
    sortBy: openSortBy,
    sortOrder: openSortOrder,
    status: openSelectedStatus,
    requestedBy: user ? [user.id.toString()] : []
  });

  // Get tickets where current user is assigned (assigned to user)
  const {
    tickets: assignedTickets,
    totalCount: assignedTotalCount,
    isLoading: assignedLoading,
    stats: assignedStats,
    fetchTickets: fetchAssignedTickets
  } = useTickets({
    dateRange: dateRange,
    page: assignedCurrentPage,
    limit: ITEMS_PER_PAGE,
    sortBy: assignedSortBy,
    sortOrder: assignedSortOrder,
    status: assignedSelectedStatus,
    assignedTo: user ? [user.id.toString()] : []
  });

  // Calculate total pages
  const openTotalPages = Math.ceil(openTotalCount / ITEMS_PER_PAGE);
  const assignedTotalPages = Math.ceil(assignedTotalCount / ITEMS_PER_PAGE);

  const handleTicketClick = (ticketId: number) => {
    navigate(`/tickets/${ticketId}`, {
      state: {
        from: '/my-workspace',
        filterState: {
          activeTab,
          openCurrentPage,
          assignedCurrentPage
        }
      }
    });
  };

  const handleCreateTicket = () => {
    navigate('/create-ticket');
  };

  const handleBulkStatusChange = async (ticketsIds: number[], newStatus: Status) => {
    await ticketService.bulkStatusUpdate(ticketsIds, newStatus, user?.id || 0);

  };

  const renderTicketView = (tickets: Ticket[], loading: boolean) => {
    if (loading) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    if (tickets.length === 0) {
      const isOpenTab = activeTab === 'open';
      return (
        <div className="text-center py-12">
          <TicketIcon className={`mx-auto h-12 w-12 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-400'}`} />
          <h3 className={`mt-2 text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-900'}`}>
            {isOpenTab ? t('pages.myWorkspace.noOpenTickets') : t('pages.myWorkspace.noAssignedTickets')}
          </h3>
          {isOpenTab && (
            <div className="mt-6">
              <button
                type="button"
                onClick={handleCreateTicket}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Plus className="-ml-1 mr-2 h-5 w-5" />
                {t('pages.myWorkspace.createFirstTicket')}
              </button>
            </div>
          )}
        </div>
      );
    }

    switch (viewMode) {
      case 'table':
        return <TicketTable tickets={tickets} currentPage={currentPage} onTicketClick={handleTicketClick} isBulkStatusChangeDisabled={true} onBulkStatusChange={handleBulkStatusChange} />;
      case 'list':
        return <TicketList tickets={tickets} currentPage={currentPage} onTicketClick={handleTicketClick} />;
      case 'kanban':
      default:
        return (
          <TicketKanbanBoard
            tickets={tickets}
            currentPage={currentPage}
            onTicketClick={handleTicketClick}
            onUpdateTicketStatus={() => { }} // Empty function for now
          />
        );
    }
  };

  const currentTickets = activeTab === 'open' ? openTickets : assignedTickets;
  const currentLoading = activeTab === 'open' ? openLoading : assignedLoading;
  const currentTotalPages = activeTab === 'open' ? openTotalPages : assignedTotalPages;
  const currentPage = activeTab === 'open' ? openCurrentPage : assignedCurrentPage;
  const setCurrentPage = activeTab === 'open' ? setOpenCurrentPage : setAssignedCurrentPage;

  return (
    <DashboardLayout>
      <Container className={`py-6 space-y-6 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
        {/* Page Header */}
        <div className="space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center space-x-3">
              <User className={`h-8 w-8 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`} />
              <div>
                <h1 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  {t('pages.myWorkspace.title')}
                </h1>
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('pages.myWorkspace.openTicketsDescription')} & {t('pages.myWorkspace.assignedTicketsDescription')}
                </p>
              </div>
            </div>
            <div className="mt-4 sm:mt-0">
              <DateRangePicker
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                onDateChange={(startDate, endDate) => setDateRange({ startDate, endDate })}
              />
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className={`rounded-lg border p-6 ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-8 w-8 rounded-md bg-blue-500 text-white">
                    <User className="h-5 w-5" />
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className={`text-sm font-medium truncate ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      {t('pages.myWorkspace.ticketsAsRequester')}
                    </dt>
                    <dd className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {openStats?.total || 0} {t('pages.myWorkspace.totalOpen')}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>

            <div className={`rounded-lg border p-6 ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-8 w-8 rounded-md bg-green-500 text-white">
                    <TicketIcon className="h-5 w-5" />
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className={`text-sm font-medium truncate ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      {t('pages.myWorkspace.ticketsAsAgent')}
                    </dt>
                    <dd className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {assignedStats?.total || 0} {t('pages.myWorkspace.totalAssigned')}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className={`border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('open')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'open'
                  ? theme === 'dark'
                    ? 'border-blue-400 text-blue-400'
                    : 'border-blue-500 text-blue-600'
                  : theme === 'dark'
                    ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                {t('pages.myWorkspace.myOpenTickets')} ({openSelectedStatus ? openStats[openSelectedStatus as keyof typeof openStats] || 0 : openStats.total})
              </button>
              <button
                onClick={() => setActiveTab('assigned')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'assigned'
                  ? theme === 'dark'
                    ? 'border-blue-400 text-blue-400'
                    : 'border-blue-500 text-blue-600'
                  : theme === 'dark'
                    ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                {t('pages.myWorkspace.myAssignedTickets')} ({assignedSelectedStatus ? assignedStats[assignedSelectedStatus as keyof typeof assignedStats] || 0 : assignedStats.total})
              </button>
            </nav>
          </div>


          <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            {activeTab === 'open'
              ? t('pages.myWorkspace.openTicketsDescription')
              : t('pages.myWorkspace.assignedTicketsDescription')
            }
          </div>
          <div className="flex justify-between items-center">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex-shrink-0">
                {/* Status Filter */}
                {activeTab === 'open' ? (
                  <select
                    value={openSelectedStatus}
                    onChange={(e) => handleStatusChange(e.target.value, 'open')}
                    className={`block w-full pl-3 pr-10 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md transition-colors ${theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600'
                      : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50'
                      }`}
                  >
                    <option value="" className={theme === 'dark' ? 'bg-gray-700' : 'bg-white'}>{t('pages.tickets.allStatuses')}</option>
                    {TICKET_STATUSES.map((status) => (
                      <option
                        key={status.value}
                        value={status.value}
                        className={theme === 'dark' ? 'bg-gray-700' : 'bg-white'}
                      >
                        {status.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <select
                    value={assignedSelectedStatus}
                    onChange={(e) => handleStatusChange(e.target.value, 'assigned')}
                    className={`block w-full pl-3 pr-10 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md transition-colors ${theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600'
                      : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50'
                      }`}
                  >
                    <option value="" className={theme === 'dark' ? 'bg-gray-700' : 'bg-white'}>{t('pages.tickets.allStatuses')}</option>
                    {TICKET_STATUSES.map((status) => (
                      <option
                        key={status.value}
                        value={status.value}
                        className={theme === 'dark' ? 'bg-gray-700' : 'bg-white'}
                      >
                        {status.label}
                      </option>
                    ))}
                  </select>
                )
                }
              </div>

              <div className="flex-shrink-0">
                {/* Sort By Filter */}
                {activeTab === 'open' ? (
                  <select
                    value={openSortBy}
                    onChange={(e) => handleSortChange(e.target.value, 'open')}
                    className={`block w-full pl-3 pr-10 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md transition-colors ${theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600'
                      : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50'
                      }`}
                  >
                    <option value="createdAt" className={theme === 'dark' ? 'bg-gray-700' : 'bg-white'}>{t('tickets.createdAt')}</option>
                    <option value="updatedAt" className={theme === 'dark' ? 'bg-gray-700' : 'bg-white'}>{t('tickets.updatedAt')}</option>
                    <option value="priority" className={theme === 'dark' ? 'bg-gray-700' : 'bg-white'}>{t('tickets.priority')}</option>
                    <option value="status" className={theme === 'dark' ? 'bg-gray-700' : 'bg-white'}>{t('tickets.columns.status')}</option>
                  </select>
                ) : (
                  <select
                    value={assignedSortBy}
                    onChange={(e) => handleSortChange(e.target.value, 'assigned')}
                    className={`block w-full pl-3 pr-10 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md transition-colors ${theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600'
                      : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50'
                      }`}
                  >
                    <option value="createdAt" className={theme === 'dark' ? 'bg-gray-700' : 'bg-white'}>{t('tickets.createdAt')}</option>
                    <option value="updatedAt" className={theme === 'dark' ? 'bg-gray-700' : 'bg-white'}>{t('tickets.updatedAt')}</option>
                    <option value="priority" className={theme === 'dark' ? 'bg-gray-700' : 'bg-white'}>{t('tickets.priority')}</option>
                    <option value="status" className={theme === 'dark' ? 'bg-gray-700' : 'bg-white'}>{t('tickets.columns.status')}</option>
                  </select>
                )}
              </div>

              {/* Sort Order Filter */}
              {activeTab === 'open' ? (
                <button
                  onClick={() => handleSortOrderChange(openSortOrder === 'asc' ? 'desc' : 'asc', 'open')}
                  className={`inline-flex items-center px-3 py-2 border text-sm leading-4 font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors ${theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  {openSortOrder === 'asc' ? '↑' : '↓'}
                </button>
              ) : (
                <button
                  onClick={() => handleSortOrderChange(assignedSortOrder === 'asc' ? 'desc' : 'asc', 'assigned')}
                  className={`inline-flex items-center px-3 py-2 border text-sm leading-4 font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors ${theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  {assignedSortOrder === 'asc' ? '↑' : '↓'}
                </button>
              )}
            </div>
            {/* View Toggle - Moved to the right */}
            <div className="flex-grow"></div>
            <div className="flex items-center">
              <span className={`mr-2 text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{t('pages.tickets.view')}:</span>
              <div className={`flex items-center rounded-lg ${theme === 'dark' ? 'bg-gray-700/70' : 'bg-gray-100'}`}>
                <button
                  onClick={() => toggleViewMode('table')}
                  className={`flex items-center justify-center px-3 py-2 rounded-md transition-all duration-200 ${viewMode === 'table'
                    ? `${theme === 'dark' ? 'bg-blue-500 text-white' : 'bg-blue-600 text-white'} shadow-sm`
                    : `${theme === 'dark' ? 'text-gray-300 hover:bg-gray-600/50' : 'text-gray-600 hover:bg-gray-200'}`
                    }`}
                  title={t('pages.tickets.tableView')}
                >
                  <i className="fas fa-list mr-1"></i>
                  <span className="text-xs font-medium">{t('pages.tickets.tableView')}</span>
                </button>
                <button
                  onClick={() => toggleViewMode('list')}
                  className={`flex items-center justify-center px-3 py-2 rounded-md transition-all duration-200 ${viewMode === 'list'
                    ? `${theme === 'dark' ? 'bg-blue-500 text-white' : 'bg-blue-600 text-white'} shadow-sm`
                    : `${theme === 'dark' ? 'text-gray-300 hover:bg-gray-600/50' : 'text-gray-600 hover:bg-gray-200'}`
                    }`}
                  title={t('pages.tickets.listView')}
                >
                  <i className="fas fa-table mr-1"></i>
                  <span className="text-xs font-medium">{t('pages.tickets.listView')}</span>
                </button>
                <button
                  onClick={() => toggleViewMode('kanban')}
                  className={`flex items-center justify-center px-3 py-2 rounded-md transition-all duration-200 ${viewMode === 'kanban'
                    ? `${theme === 'dark' ? 'bg-blue-500 text-white' : 'bg-blue-600 text-white'} shadow-sm`
                    : `${theme === 'dark' ? 'text-gray-300 hover:bg-gray-600/50' : 'text-gray-600 hover:bg-gray-200'}`
                    }`}
                  title={t('pages.tickets.kanbanView')}
                >
                  <i className="fas fa-columns mr-1"></i>
                  <span className="text-xs font-medium">{t('pages.tickets.kanbanView')}</span>
                </button>
              </div>
            </div>
          </div>

          {/* Tickets Display */}
          <div className="space-y-6">
            {renderTicketView(currentTickets, currentLoading)}

            {/* Pagination */}
            {currentTotalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={currentTotalPages}
                onPageChange={setCurrentPage}
              />
            )}
          </div>
        </div>
      </Container>
    </DashboardLayout>
  );
}
